import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatCAD, formatDate } from "@/lib/utils-cart";
import { GatewayBadge, PaymentStatusBadge } from "@/components/Badges";
import { Truck, X } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);
  const [tracking, setTracking] = useState({ tracking_number: "", carrier: "Canada Post", status: "shipped" });

  const load = () => api.get("/orders/mine").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const saveShipment = async (orderId) => {
    await api.patch(`/orders/${orderId}/shipment`, tracking);
    toast.success("Shipment updated");
    setActive(null);
    load();
  };

  return (
    <DashboardLayout title="Orders" subtitle="All orders involving your products, store, or account.">
      <div className="v-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="p-3">Order</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Status</th>
              <th className="p-3">Gateway</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3">Shipment</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="p-3">
                  <div className="font-mono text-xs">{o.order_number}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(o.created_at)}</div>
                </td>
                <td className="p-3">
                  <div className="text-primary font-medium">{o.buyer_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{o.buyer_email}</div>
                </td>
                <td className="p-3"><PaymentStatusBadge status={o.payment_status} testid={`order-status-${o.id}`} /></td>
                <td className="p-3"><GatewayBadge gateway={o.gateway} testid={`order-gateway-${o.id}`} /></td>
                <td className="p-3 text-right font-mono">{formatCAD(o.total)}</td>
                <td className="p-3 text-xs">
                  {o.shipment?.tracking_number ? (
                    <div>
                      <div className="font-mono text-primary">{o.shipment.tracking_number}</div>
                      <div className="text-muted-foreground">{o.shipment.carrier} · {o.shipment.status}</div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Not shipped</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => setActive(o)} className="v-btn-outline text-xs" data-testid={`order-details-${o.id}`}>Details</button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-card max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-sm border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-mono text-sm text-muted-foreground">{active.order_number}</div>
                <h2 className="font-display text-2xl font-bold text-primary mt-1">{formatCAD(active.total)}</h2>
                <div className="mt-2 flex gap-2">
                  <PaymentStatusBadge status={active.payment_status} />
                  <GatewayBadge gateway={active.gateway} />
                </div>
              </div>
              <button onClick={() => setActive(null)} className="v-btn-ghost"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="label-eyebrow mb-2">Customer</div>
                <div className="text-sm">{active.buyer_name} · <span className="font-mono">{active.buyer_email}</span></div>
              </div>
              <div>
                <div className="label-eyebrow mb-2">Shipping</div>
                <div className="text-sm font-mono">{active.shipping_address?.street}, {active.shipping_address?.city} {active.shipping_address?.province} {active.shipping_address?.postal_code}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-2">Items</div>
                {active.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-border last:border-0 text-sm">
                    <img src={it.image} alt="" className="w-10 h-10 object-cover rounded-sm" />
                    <div className="flex-1">{it.title} <span className="text-muted-foreground font-mono">× {it.quantity}</span></div>
                    <div className="font-mono">{formatCAD(it.line_total)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-4">
                <div className="label-eyebrow mb-3">Update shipment</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <input placeholder="Tracking number" className="v-input" defaultValue={active.shipment?.tracking_number || ""} onChange={(e) => setTracking({...tracking, tracking_number: e.target.value})} data-testid="order-tracking-number" />
                  <input placeholder="Carrier" className="v-input" defaultValue={active.shipment?.carrier || "Canada Post"} onChange={(e) => setTracking({...tracking, carrier: e.target.value})} data-testid="order-tracking-carrier" />
                  <select className="v-input" defaultValue={active.shipment?.status || "shipped"} onChange={(e) => setTracking({...tracking, status: e.target.value})} data-testid="order-tracking-status">
                    <option value="pending">Pending</option>
                    <option value="label_created">Label created</option>
                    <option value="shipped">Shipped</option>
                    <option value="in_transit">In transit</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>
                <button onClick={() => saveShipment(active.id)} className="v-btn-primary mt-3" data-testid="order-save-shipment">
                  <Truck size={14} /> Save shipment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
